import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Copy,
  Edit3,
  ExternalLink,
  FileQuestion,
  HelpCircle,
  Loader2,
  LockKeyhole,
  MessageSquare,
  Plus,
  RefreshCw,
  Route,
  Search,
  ShieldCheck,
  Tag,
  ToggleLeft,
  ToggleRight,
  Trash2,
  X,
} from 'lucide-react';
import { supabase } from '../config/supabase';
import { useUserStore } from '../stores/user-store';
import { cn } from '../utils/cn';
import { isSuperAdmin } from '../utils/permissions';

interface FaqSpaceOverview {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
  item_count: number;
  tag_count: number;
  question_count: number;
  is_default: boolean;
  is_empty: boolean;
  slug_locked: boolean;
}

interface SpaceFormState {
  title: string;
  slug: string;
  description: string;
  is_active: boolean;
  sort_order: number;
}

const EMPTY_FORM: SpaceFormState = {
  title: '',
  slug: '',
  description: '',
  is_active: true,
  sort_order: 30,
};

const RESERVED_TOP_LEVEL_SLUGS = new Set([
  'uadmin',
  'cn',
  'intl',
  'login',
  'register',
  'reset-password',
  'portal',
  'poster',
  'whiteboard',
  'price_intel',
  'price-intel',
]);

function normalizeSlug(value: string) {
  return value.trim().toLowerCase();
}

function validateSlug(slug: string) {
  if (!slug) return '请输入路由 slug';
  if (slug !== 'faq' && !/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?-faq$/.test(slug)) {
    return 'slug 只允许 faq，或小写且以 -faq 结尾，例如 crm-faq';
  }
  if (slug !== 'faq' && RESERVED_TOP_LEVEL_SLUGS.has(slug)) {
    return '该 slug 与系统顶层路由冲突';
  }
  return '';
}

function buildSpaceUrl(origin: string, slug: string, admin = false) {
  return `${origin}/${slug}${admin ? '/uadmin' : ''}`;
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

const FaqSpacesManagementPage: React.FC = () => {
  const { profile: currentUser } = useUserStore();
  const canManageSpaces = currentUser
    ? isSuperAdmin(currentUser.role, currentUser.can_access_all_tenants)
    : false;
  const [spaces, setSpaces] = useState<FaqSpaceOverview[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [query, setQuery] = useState('');
  const [editingSpace, setEditingSpace] = useState<FaqSpaceOverview | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<SpaceFormState>(EMPTY_FORM);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const origin = useMemo(() => window.location.origin, []);

  const showToast = useCallback((type: 'success' | 'error', message: string) => {
    setToast({ type, message });
    window.setTimeout(() => setToast(null), 3500);
  }, []);

  const fetchSpaces = useCallback(async () => {
    if (!canManageSpaces) return;

    try {
      setIsLoading(true);
      const { data, error } = await supabase.rpc('list_faq_space_overviews');
      if (error) throw error;
      setSpaces((data || []) as FaqSpaceOverview[]);
    } catch (error) {
      console.error('获取 FAQ 页面列表失败:', error);
      showToast('error', `获取 FAQ 页面列表失败：${(error as Error).message}`);
    } finally {
      setIsLoading(false);
    }
  }, [canManageSpaces, showToast]);

  useEffect(() => {
    if (currentUser) {
      fetchSpaces();
    }
  }, [currentUser, fetchSpaces]);

  const filteredSpaces = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    if (!keyword) return spaces;
    return spaces.filter((space) =>
      space.title.toLowerCase().includes(keyword) ||
      space.slug.toLowerCase().includes(keyword) ||
      (space.description || '').toLowerCase().includes(keyword)
    );
  }, [query, spaces]);

  const summary = useMemo(() => ({
    total: spaces.length,
    active: spaces.filter((space) => space.is_active).length,
    items: spaces.reduce((sum, space) => sum + Number(space.item_count || 0), 0),
    questions: spaces.reduce((sum, space) => sum + Number(space.question_count || 0), 0),
  }), [spaces]);

  const openCreateForm = () => {
    const nextSort = spaces.length
      ? Math.max(...spaces.map((space) => Number(space.sort_order || 0))) + 10
      : 10;
    setEditingSpace(null);
    setForm({ ...EMPTY_FORM, sort_order: nextSort });
    setShowForm(true);
  };

  const openEditForm = (space: FaqSpaceOverview) => {
    setEditingSpace(space);
    setForm({
      title: space.title,
      slug: space.slug,
      description: space.description || '',
      is_active: space.is_default ? true : space.is_active,
      sort_order: Number(space.sort_order || 0),
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingSpace(null);
    setForm(EMPTY_FORM);
  };

  const getFormError = () => {
    const title = form.title.trim();
    const slug = normalizeSlug(form.slug);
    const slugDisabled = Boolean(editingSpace?.slug_locked);

    if (!title) return '请输入 FAQ 页面名称';
    if (!slugDisabled) {
      const slugError = validateSlug(slug);
      if (slugError) return slugError;
      const duplicated = spaces.some((space) => space.id !== editingSpace?.id && space.slug === slug);
      if (duplicated) return '该 slug 已存在，请换一个路由';
    }
    if (!Number.isFinite(Number(form.sort_order))) return '排序必须是数字';
    return '';
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const errorMessage = getFormError();
    if (errorMessage) {
      showToast('error', errorMessage);
      return;
    }

    const slugDisabled = Boolean(editingSpace?.slug_locked);
    const payload: Record<string, string | boolean | number | null> = {
      title: form.title.trim(),
      description: form.description.trim() || null,
      is_active: editingSpace?.is_default ? true : form.is_active,
      sort_order: Number(form.sort_order || 0),
    };

    if (!slugDisabled) {
      payload.slug = normalizeSlug(form.slug);
    }

    try {
      setIsSaving(true);
      if (editingSpace) {
        const { error } = await supabase
          .from('faq_spaces')
          .update(payload)
          .eq('id', editingSpace.id);
        if (error) throw error;
        showToast('success', 'FAQ 页面已更新');
      } else {
        const { error } = await supabase
          .from('faq_spaces')
          .insert(payload);
        if (error) throw error;
        showToast('success', 'FAQ 页面已创建，新空间初始为空');
      }

      closeForm();
      await fetchSpaces();
    } catch (error) {
      console.error('保存 FAQ 页面失败:', error);
      showToast('error', `保存失败：${(error as Error).message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleActive = async (space: FaqSpaceOverview) => {
    if (space.is_default) {
      showToast('error', '默认 FAQ 空间不能停用');
      return;
    }

    try {
      const { error } = await supabase
        .from('faq_spaces')
        .update({ is_active: !space.is_active })
        .eq('id', space.id);
      if (error) throw error;
      showToast('success', space.is_active ? 'FAQ 页面已停用' : 'FAQ 页面已启用');
      await fetchSpaces();
    } catch (error) {
      console.error('切换 FAQ 页面状态失败:', error);
      showToast('error', `状态更新失败：${(error as Error).message}`);
    }
  };

  const handleDelete = async (space: FaqSpaceOverview) => {
    if (space.is_default) {
      showToast('error', '默认 FAQ 空间不能删除');
      return;
    }
    if (!space.is_empty) {
      showToast('error', '该空间已有 FAQ、标签或留言，不能删除，请改为停用');
      return;
    }

    const confirmed = window.confirm(`确认删除空 FAQ 页面 /${space.slug}？此操作不可撤销。`);
    if (!confirmed) return;

    try {
      const { error } = await supabase
        .from('faq_spaces')
        .delete()
        .eq('id', space.id);
      if (error) throw error;
      showToast('success', '空 FAQ 页面已删除');
      await fetchSpaces();
    } catch (error) {
      console.error('删除 FAQ 页面失败:', error);
      showToast('error', `删除失败：${(error as Error).message}`);
    }
  };

  const copyUrl = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      showToast('success', '链接已复制');
    } catch (error) {
      console.error('复制链接失败:', error);
      showToast('error', '复制失败，请手动复制浏览器地址');
    }
  };

  if (!currentUser) {
    return (
      <div className="min-h-[420px] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-sky-500" />
      </div>
    );
  }

  if (!canManageSpaces) {
    return (
      <div className="max-w-2xl mx-auto mt-16 rounded-xl border border-amber-200 bg-amber-50 p-8 text-center">
        <ShieldCheck className="w-12 h-12 mx-auto text-amber-600" />
        <h1 className="mt-4 text-xl font-semibold text-slate-900">无权访问 FAQ 页面管理</h1>
        <p className="mt-2 text-sm text-slate-600">
          这里只开放给全局平台管理员。普通 FAQ 管理员仍可从具体空间的 /faq/uadmin 或 /sql-faq/uadmin 管理内容。
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {toast && (
        <div className={cn(
          'fixed top-5 right-5 z-50 flex items-center gap-2 rounded-lg px-4 py-3 text-sm shadow-lg',
          toast.type === 'success' ? 'bg-emerald-600 text-white' : 'bg-rose-600 text-white'
        )}>
          {toast.type === 'success' ? <CheckCircle2 size={18} /> : <AlertTriangle size={18} />}
          <span>{toast.message}</span>
        </div>
      )}

      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-sky-100 text-sky-700">
              <HelpCircle size={22} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">FAQ 页面管理</h1>
              <p className="mt-1 text-sm text-slate-500">
                管理 FAQ 页面的路由、名称和启用状态；内容仍在各自的 /slug/uadmin 中维护。
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={fetchSpaces}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            <RefreshCw size={16} />
            刷新
          </button>
          <button
            type="button"
            onClick={openCreateForm}
            className="inline-flex items-center gap-2 rounded-lg bg-sky-600 px-3 py-2 text-sm font-semibold text-white hover:bg-sky-700"
          >
            <Plus size={16} />
            新建 FAQ 页面
          </button>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-4">
        {[
          { label: 'FAQ 页面', value: summary.total, icon: Route },
          { label: '已启用', value: summary.active, icon: ToggleRight },
          { label: 'FAQ 条目', value: summary.items, icon: FileQuestion },
          { label: '留言', value: summary.questions, icon: MessageSquare },
        ].map((card) => (
          <div key={card.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500">{card.label}</span>
              <card.icon size={18} className="text-slate-400" />
            </div>
            <div className="mt-3 text-2xl font-bold text-slate-900">{card.value}</div>
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-col gap-3 border-b border-slate-200 p-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-base font-semibold text-slate-900">页面列表</h2>
            <p className="mt-1 text-sm text-slate-500">只有空空间可以删除；已有数据后 slug 会自动锁定。</p>
          </div>
          <div className="relative w-full md:w-80">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="搜索名称、slug 或描述"
              className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">名称</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">路由</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">统计</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">状态</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">更新时间</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-slate-500">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-16 text-center text-slate-500">
                    <Loader2 className="mx-auto mb-3 h-8 w-8 animate-spin text-sky-500" />
                    正在加载 FAQ 页面列表
                  </td>
                </tr>
              ) : filteredSpaces.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-16 text-center text-slate-500">
                    没有找到匹配的 FAQ 页面
                  </td>
                </tr>
              ) : filteredSpaces.map((space) => {
                const userUrl = buildSpaceUrl(origin, space.slug);
                const adminUrl = buildSpaceUrl(origin, space.slug, true);
                const isAccessDisabled = !space.is_active;

                return (
                  <tr key={space.id} className="hover:bg-slate-50/70">
                    <td className="px-4 py-4 align-top">
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100 text-slate-600">
                          <HelpCircle size={18} />
                        </div>
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-semibold text-slate-900">{space.title}</span>
                            {space.is_default && (
                              <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-2 py-0.5 text-xs font-medium text-sky-700">
                                <LockKeyhole size={12} />
                                默认
                              </span>
                            )}
                          </div>
                          {space.description && (
                            <p className="mt-1 max-w-xs truncate text-sm text-slate-500">{space.description}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 align-top">
                      <div className="space-y-1 text-sm">
                        <div className="font-mono text-slate-900">/{space.slug}</div>
                        <div className="font-mono text-slate-500">/{space.slug}/uadmin</div>
                      </div>
                    </td>
                    <td className="px-4 py-4 align-top">
                      <div className="flex flex-wrap gap-2 text-xs">
                        <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-1 text-slate-700">
                          <FileQuestion size={13} />
                          {space.item_count} FAQ
                        </span>
                        <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-1 text-slate-700">
                          <Tag size={13} />
                          {space.tag_count} 标签
                        </span>
                        <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-1 text-slate-700">
                          <MessageSquare size={13} />
                          {space.question_count} 留言
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-4 align-top">
                      <span className={cn(
                        'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold',
                        space.is_active
                          ? 'bg-emerald-50 text-emerald-700'
                          : 'bg-slate-100 text-slate-500'
                      )}>
                        {space.is_active ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
                        {space.is_active ? '已启用' : '已停用'}
                      </span>
                    </td>
                    <td className="px-4 py-4 align-top text-sm text-slate-500">
                      {formatDate(space.updated_at)}
                    </td>
                    <td className="px-4 py-4 align-top">
                      <div className="flex justify-end gap-1">
                        <a
                          href={isAccessDisabled ? undefined : userUrl}
                          target="_blank"
                          rel="noreferrer"
                          aria-disabled={isAccessDisabled}
                          className={cn(
                            'inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-100',
                            isAccessDisabled && 'pointer-events-none opacity-40'
                          )}
                          title="打开用户端"
                        >
                          <ExternalLink size={15} />
                        </a>
                        <a
                          href={isAccessDisabled ? undefined : adminUrl}
                          target="_blank"
                          rel="noreferrer"
                          aria-disabled={isAccessDisabled}
                          className={cn(
                            'inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-100',
                            isAccessDisabled && 'pointer-events-none opacity-40'
                          )}
                          title="打开管理端"
                        >
                          <ShieldCheck size={15} />
                        </a>
                        <button
                          type="button"
                          onClick={() => copyUrl(userUrl)}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-100"
                          title="复制用户端链接"
                        >
                          <Copy size={15} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleToggleActive(space)}
                          disabled={space.is_default}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40"
                          title={space.is_active ? '停用' : '启用'}
                        >
                          {space.is_active ? <ToggleRight size={15} /> : <ToggleLeft size={15} />}
                        </button>
                        <button
                          type="button"
                          onClick={() => openEditForm(space)}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-100"
                          title="编辑"
                        >
                          <Edit3 size={15} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(space)}
                          disabled={space.is_default || !space.is_empty}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-rose-600 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-40"
                          title={space.is_empty ? '删除空空间' : '已有数据，不能删除'}
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/40 p-4">
          <form
            onSubmit={handleSubmit}
            className="w-full max-w-2xl rounded-xl bg-white shadow-2xl"
          >
            <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">
                  {editingSpace ? '编辑 FAQ 页面' : '新建 FAQ 页面'}
                </h2>
                <p className="mt-1 text-sm text-slate-500">这里只配置页面入口，不复制任何 FAQ 内容。</p>
              </div>
              <button
                type="button"
                onClick={closeForm}
                className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-5 px-6 py-5">
              <div className="grid gap-4 md:grid-cols-2">
                <label className="space-y-2">
                  <span className="text-sm font-medium text-slate-700">名称</span>
                  <input
                    value={form.title}
                    onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))}
                    placeholder="例如 CRM FAQ 支持中心"
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
                  />
                </label>

                <label className="space-y-2">
                  <span className="text-sm font-medium text-slate-700">slug</span>
                  <input
                    value={form.slug}
                    onChange={(event) => setForm((prev) => ({ ...prev, slug: event.target.value }))}
                    disabled={Boolean(editingSpace?.slug_locked)}
                    placeholder="crm-faq"
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 font-mono text-sm outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100 disabled:bg-slate-100 disabled:text-slate-500"
                  />
                  {editingSpace?.slug_locked && (
                    <p className="flex items-center gap-1 text-xs text-slate-500">
                      <LockKeyhole size={13} />
                      该空间已有数据或是默认空间，slug 已锁定
                    </p>
                  )}
                </label>
              </div>

              <label className="space-y-2 block">
                <span className="text-sm font-medium text-slate-700">描述</span>
                <textarea
                  value={form.description}
                  onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
                  rows={3}
                  placeholder="用于后台识别这个 FAQ 页面的用途"
                  className="w-full resize-none rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
                />
              </label>

              <div className="grid gap-4 md:grid-cols-2">
                <label className="space-y-2">
                  <span className="text-sm font-medium text-slate-700">排序</span>
                  <input
                    type="number"
                    value={form.sort_order}
                    onChange={(event) => setForm((prev) => ({ ...prev, sort_order: Number(event.target.value) }))}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
                  />
                </label>

                <div className="space-y-2">
                  <span className="text-sm font-medium text-slate-700">启用状态</span>
                  <button
                    type="button"
                    disabled={editingSpace?.is_default}
                    onClick={() => setForm((prev) => ({ ...prev, is_active: !prev.is_active }))}
                    className={cn(
                      'flex w-full items-center justify-between rounded-lg border px-3 py-2 text-sm font-medium',
                      form.is_active
                        ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                        : 'border-slate-200 bg-slate-50 text-slate-600',
                      editingSpace?.is_default && 'cursor-not-allowed opacity-70'
                    )}
                  >
                    <span>{form.is_active ? '已启用' : '已停用'}</span>
                    {form.is_active ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
                  </button>
                </div>
              </div>

              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <div className="text-sm font-medium text-slate-700">链接预览</div>
                <div className="mt-3 grid gap-2 text-sm md:grid-cols-2">
                  <div className="rounded-md bg-white px-3 py-2 font-mono text-slate-700">
                    /{normalizeSlug(form.slug) || 'slug'}
                  </div>
                  <div className="rounded-md bg-white px-3 py-2 font-mono text-slate-700">
                    /{normalizeSlug(form.slug) || 'slug'}/uadmin
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-6 py-4">
              <button
                type="button"
                onClick={closeForm}
                className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                取消
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className="inline-flex items-center gap-2 rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isSaving && <Loader2 size={16} className="animate-spin" />}
                保存
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default FaqSpacesManagementPage;
